import type { TFunction } from "i18next";
import { FaSignature } from "react-icons/fa";
import { FaCalendar } from "react-icons/fa6";
import {
  MdAlarm,
  MdAssessment,
  MdBugReport,
  MdContacts,
  MdEmail,
  MdErrorOutline,
  MdEventNote,
  MdEventSeat,
  MdFace,
  MdFileUpload,
  MdForum,
  MdHome,
  MdInventory,
  MdLightbulbOutline,
  MdMessage,
  MdNotificationImportant,
  MdOutlineInsertDriveFile,
  MdOutlineQuestionAnswer,
  MdPayment,
  MdSettings,
  MdShowChart,
  MdSmartphone,
  MdSyncAlt,
  MdTask,
  MdViewKanban,
  MdWork,
} from "react-icons/md";

import { acdCallsPath, acdDashboardPath } from "@path/acd_paths";
import { adminPath } from "@path/admin_paths";
import { calendarPath } from "@path/calendar_paths";
import { customerFeedbacksPath } from "@path/customerFeedbacksPaths";
import { digitalSignaturesPath } from "@path/digital_signature_paths";
import { documentsPath } from "@path/document_paths";
import { fileExchangePath } from "@path/fileExchangePaths";
import { hotseatPath } from "@path/hotseat_path";
import { inventoryItemTypesPath } from "@path/inventoryPaths";
import { invoicesPath } from "@path/invoices_paths";
import { jobsPath } from "@path/jobs_path";
import { knowledgebaseQuestionsPath } from "@path/knowledgebase_paths";
import { knowledgebaseNgPath } from "@path/knowledgebaseNgPaths";
import { mailsPath } from "@path/mails_paths";
import { messagesPath } from "@path/messages_paths";
import { projectNewsPath } from "@path/project_news_path";
import { projectUploadsPath } from "@path/project_uploads_paths";
import { projectReportsPath } from "@path/projectReportPaths";
import { punchlistPath } from "@path/punchlist_entry_paths";
import { reminderNotificationsPath } from "@path/reminders_path";
import { rosterPath } from "@path/roster_paths";
import { staffPath } from "@path/staff_paths";
import { statsPath } from "@path/stats_paths";
import { ticketsPath } from "@path/tickets_paths";
import { todosPath } from "@path/todos_paths";

import { may } from "@/abilities";
import { isActive, isGJUDemo } from "@/new_utils";
import { contactsPath, customerContactsPath, pipelinesPath, rootPath } from "@/path_helpers";

export const NAV_GROUP_ORDER = ["main", "analytics", "resources", "staff", "system", "more"] as const;

export type NavGroupKey = (typeof NAV_GROUP_ORDER)[number];

export type NavItem = {
  to: string;
  text: string;
  icon?: any;
  active: boolean;
  group: NavGroupKey;
};

export const getSubnavItems = (t: TFunction, session: SessionInterface, location: string): NavItem[] => {
  if (!session.currentCustomer || !session.currentProject) return [];

  const items: NavItem[] = [];

  const add = (item: NavItem) => items.push(item);

  add({
    to: rootPath(session),
    text: t("nav.home"),
    icon: MdHome,
    active: location === `/${session.currentCustomer.identifier}/${session.currentProject.identifier}`,
    group: "main",
  });

  if (may(session, "contacts"))
    add({
      to: contactsPath(session),
      icon: MdContacts,
      text: t("contacts:name"),
      active: isActive(location, "/contacts"),
      group: "main",
    });

  if (may(session, "contacts") && isGJUDemo(session))
    add({
      to: pipelinesPath(session),
      icon: MdViewKanban,
      text: t("nav.pipelines"),
      active: isActive(location, "/pipelines"),
      group: "main",
    });

  if (may(session, "customer-contacts"))
    add({
      to: customerContactsPath(session),
      icon: MdForum,
      text: t("nav.customer_contacts"),
      active: isActive(location, "/customer-contacts"),
      group: "main",
    });

  if (may(session, "mails"))
    add({
      to: mailsPath(session),
      icon: MdEmail,
      text: t("nav.emails"),
      active: isActive(location, "/mails"),
      group: "main",
    });

  if (may(session, "todos/todos"))
    add({
      to: todosPath(session),
      icon: MdTask,
      text: t("nav.todos"),
      active: isActive(location, "/todos"),
      group: "main",
    });

  if (may(session, "calendar/events"))
    add({
      to: calendarPath(session),
      icon: FaCalendar,
      text: t("nav.calendar"),
      active: isActive(location, "/calendar"),
      group: "main",
    });

  if (may(session, "customer-feedbacks"))
    add({
      to: customerFeedbacksPath(session),
      icon: MdOutlineQuestionAnswer,
      text: t("translation:nav.customerFeedbacks"),
      active: isActive(location, "/customer-feedbacks"),
      group: "main",
    });

  if (may(session, "punchlist"))
    add({
      to: punchlistPath(session),
      icon: MdErrorOutline,
      text: "Fehlerliste",
      active: isActive(location, "/punchlist"),
      group: "main",
    });

  if (may(session, "stats"))
    add({
      to: statsPath(session),
      icon: MdShowChart,
      text: t("nav.stats"),
      active: isActive(location, "/stats"),
      group: "analytics",
    });

  if (may(session, "project-reports"))
    add({
      to: projectReportsPath(session),
      icon: MdAssessment,
      text: t("nav.projectReports"),
      active: isActive(location, "/project-reports"),
      group: "analytics",
    });

  if (may(session, "acd/calls") || may(session, "acd/dashboard"))
    add({
      to: may(session, "acd/calls") ? acdCallsPath(session) : acdDashboardPath(session),
      icon: MdSmartphone,
      text: t("nav.acd"),
      active: isActive(location, "/acd"),
      group: "analytics",
    });

  if (may(session, "knowledgebase"))
    add({
      to: knowledgebaseQuestionsPath(session),
      icon: MdLightbulbOutline,
      text: t("nav.knowledgebase"),
      active: isActive(location, "/knowledgebase"),
      group: "resources",
    });

  if (may(session, "knowledgebase_ng"))
    add({
      to: knowledgebaseNgPath(session),
      icon: MdLightbulbOutline,
      text: t("nav.knowledgebase_ng"),
      active: isActive(location, "/knowledgebase-ng"),
      group: "resources",
    });

  if (may(session, "documents"))
    add({
      to: documentsPath(session),
      icon: MdOutlineInsertDriveFile,
      text: t("nav.documents"),
      active: isActive(location, "/documents"),
      group: "resources",
    });

  if (may(session, "project/uploads"))
    add({
      to: projectUploadsPath(session),
      icon: MdFileUpload,
      text: t("nav.uploads"),
      active: isActive(location, "/project/uploads"),
      group: "resources",
    });

  if (may(session, "file-exchange"))
    add({
      to: fileExchangePath(session),
      icon: MdSyncAlt,
      text: t("translation:nav.fileExchange"),
      active: isActive(location, "/file-exchange"),
      group: "resources",
    });

  if (may(session, "inventory/item-types"))
    add({
      to: inventoryItemTypesPath(session),
      icon: MdInventory,
      text: t("translation:nav.inventory"),
      active: isActive(location, "/inventory"),
      group: "resources",
    });

  if (may(session, "project-news"))
    add({
      to: projectNewsPath(session),
      icon: MdEventNote,
      text: t("nav.projectNews"),
      active: isActive(location, "/project-news"),
      group: "resources",
    });

  if (may(session, "staff"))
    add({
      to: staffPath(session),
      icon: MdFace,
      text: t("nav.staff"),
      active: isActive(location, "/staff"),
      group: "staff",
    });

  if (may(session, "roster"))
    add({
      to: rosterPath(session),
      icon: MdAlarm,
      text: t("nav.roster"),
      active: isActive(location, "/roster"),
      group: "staff",
    });

  if (may(session, "hotseat/requests"))
    add({
      to: hotseatPath(session),
      icon: MdEventSeat,
      text: t("translation:nav.hotseat"),
      active: isActive(location, "/hotseat"),
      group: "staff",
    });

  if (may(session, "digital-signatures"))
    add({
      to: digitalSignaturesPath(session),
      icon: FaSignature,
      text: t("nav.digital_signatures"),
      active: isActive(location, "/digital-signatures"),
      group: "staff",
    });

  if (may(session, "admin"))
    add({
      to: adminPath(session),
      icon: MdSettings,
      text: t("nav.administration"),
      active: isActive(location, "/admin"),
      group: "system",
    });

  if (may(session, "tickets"))
    add({
      to: ticketsPath(session),
      icon: MdBugReport,
      text: t("nav.tickets"),
      active: isActive(location, "/tickets"),
      group: "system",
    });

  if (may(session, "reminder-notifications"))
    add({
      to: reminderNotificationsPath(session),
      icon: MdNotificationImportant,
      text: t("nav.reminder_notifications"),
      active: isActive(location, "/reminder-notifications"),
      group: "system",
    });

  if (may(session, "invoices"))
    add({
      to: invoicesPath(session),
      icon: MdPayment,
      text: t("nav.invoices"),
      active: isActive(location, "/invoices"),
      group: "more",
    });

  if (may(session, "messages"))
    add({
      to: messagesPath(session),
      icon: MdMessage,
      text: t("nav.messages"),
      active: isActive(location, "/messages"),
      group: "more",
    });

  if (may(session, "jobs"))
    add({
      to: jobsPath(session),
      icon: MdWork,
      text: t("nav.jobs"),
      active: isActive(location, "/jobs"),
      group: "more",
    });

  return items;
};
